import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { AttachmentPreview } from "../components/AttachmentPreview";
import { useAuth } from "../lib/auth";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { CATEGORIES, CATEGORY_LABEL } from "../lib/labels";
import { CategoryBadge } from "../components/Badges";
import { toast } from "sonner";
import { Lightning, Plus, PencilSimple, Trash, MagnifyingGlass, Paperclip, DownloadSimple, X, FileImage, FilePdf, File as FileIcon, CheckCircle, XCircle, Clock, Warning, CaretDown, CaretRight } from "@phosphor-icons/react";

const humanSize = (n) => {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(2)} Mo`;
};

const FILE_ICON = (ct) => {
  if (!ct) return FileIcon;
  if (ct.startsWith("image/")) return FileImage;
  if (ct === "application/pdf") return FilePdf;
  return FileIcon;
};

export default function Macros() {
  const { user } = useAuth();
  const [macros, setMacros] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "autre", body: "" });
  const [attBusy, setAttBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    const r = await api.get("/macros", { params: q ? { q } : {} });
    setMacros(r.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", category: "autre", body: "" }); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ name: m.name, category: m.category, body: m.body }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/macros/${editing.id}`, form);
        toast.success("Macro mise à jour");
        setOpen(false); load();
      } else {
        const r = await api.post("/macros", form);
        toast.success("Macro créée");
        // Keep dialog open in edit mode so user can now add attachments
        setEditing(r.data);
        load();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer la macro "${m.name}" ?`)) return;
    try {
      await api.delete(`/macros/${m.id}`);
      toast.success("Supprimée"); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Suppression impossible");
    }
  };

  const approve = async (m) => {
    try {
      await api.post(`/macros/${m.id}/approve`);
      toast.success("Macro approuvée — visible par toute l'équipe");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approbation impossible");
    }
  };

  const reject = async (m) => {
    const reason = window.prompt("Motif de rejet (visible pour l'auteur) :", "Non conforme à la charte");
    if (reason === null) return;
    try {
      await api.post(`/macros/${m.id}/reject`, { reason });
      toast.success("Macro rejetée");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rejet impossible");
    }
  };

  const uploadFiles = async (fileList) => {
    if (!editing?.id || !fileList?.length) return;
    setAttBusy(true);
    try {
      const newlyAdded = [];
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/macros/${editing.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newlyAdded.push(r.data);
      }
      const merged = { ...editing, attachments: [...(editing.attachments || []), ...newlyAdded] };
      setEditing(merged);
      toast.success(`${fileList.length} fichier${fileList.length > 1 ? "s" : ""} ajouté${fileList.length > 1 ? "s" : ""}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    } finally {
      setAttBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = async (att) => {
    if (!editing?.id) return;
    if (!window.confirm(`Supprimer la pièce jointe "${att.original_filename}" ?`)) return;
    try {
      await api.delete(`/macros/${editing.id}/attachments/${att.id}`);
      const merged = { ...editing, attachments: (editing.attachments || []).filter((a) => a.id !== att.id) };
      setEditing(merged);
      toast.success("Pièce jointe supprimée");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Suppression impossible");
    }
  };

  const openAttachment = (att) => {
    setPreview({ name: att.original_filename, type: att.content_type, path: `/macros/attachments/${att.id}` });
  };

  const downloadAttachment = async (att) => {
    try {
      const r = await api.get(`/macros/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <div className="space-y-6">
      <AttachmentPreview file={preview} onClose={() => setPreview(null)} />
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Réponses enregistrées</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Macros</h1>
          <p className="text-sm text-muted-foreground mt-1">Réutilisez vos réponses fréquentes en un clic depuis n&apos;importe quel ticket.</p>
        </div>
        <Button data-testid="new-macro-btn" onClick={openNew}><Plus size={16} className="mr-2"/> Nouvelle macro</Button>
      </header>

      <Card>
        <CardContent className="pt-6 relative">
          <MagnifyingGlass size={16} className="absolute left-7 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input data-testid="macro-search" className="pl-9" placeholder="Rechercher une macro…"
            value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}/>
        </CardContent>
      </Card>

      <div className="rounded-xl border bg-card divide-y overflow-hidden" data-testid="macro-list">
        {macros.map((m) => {
          const status = m.status || "approved";
          const isAuthor = m.author_id === user?.id;
          const canEdit = user?.role === "admin" || isAuthor;
          const canDelete = user?.role === "admin" || isAuthor;
          const isOpen = expanded === m.id;
          const nAtt = (m.attachments || []).length;
          return (
          <div key={m.id} data-testid={`macro-${m.id}`}
            className={`px-3 py-2 transition-colors hover:bg-muted/40 ${status === "pending" ? "bg-amber-50/40 dark:bg-amber-950/10" : status === "rejected" ? "bg-red-50/40 dark:bg-red-950/10" : ""}`}>
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 min-w-0">
              <button type="button" onClick={() => setExpanded(isOpen ? null : m.id)} className="flex min-w-0 basis-full sm:basis-auto flex-1 items-center gap-2 text-left" data-testid={`macro-toggle-${m.id}`}>
                {isOpen ? <CaretDown size={12} className="shrink-0 text-muted-foreground"/> : <CaretRight size={12} className="shrink-0 text-muted-foreground"/>}
                <Lightning size={14} weight="fill" className="shrink-0 text-orange-500"/>
                <span className="font-medium truncate sm:max-w-[30%]">{m.name}</span>
                <span className="hidden md:inline text-sm text-muted-foreground truncate min-w-0 flex-1">{(m.body || "").replace(/\s+/g, " ")}</span>
              </button>
              <div className="ml-auto flex shrink-0 items-center gap-2">
                <span className="hidden sm:inline-flex"><CategoryBadge value={m.category}/></span>
                {status === "pending" && (
                  <span data-testid={`macro-status-${m.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.65rem] font-semibold uppercase bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800">
                    <Clock size={10} weight="fill"/> En attente
                  </span>
                )}
                {status === "rejected" && (
                  <span data-testid={`macro-status-${m.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.65rem] font-semibold uppercase bg-red-100 text-red-800 border border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800">
                    <XCircle size={10} weight="fill"/> Rejetée
                  </span>
                )}
                {status === "approved" && (
                  <span data-testid={`macro-status-${m.id}`} className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.65rem] font-semibold uppercase bg-emerald-100 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800">
                    <CheckCircle size={10} weight="fill"/> Publiée
                  </span>
                )}
                {nAtt === 1 && (
                  <button type="button" onClick={() => openAttachment(m.attachments[0])} title={`Ouvrir ${m.attachments[0].original_filename}`}
                    className="inline-flex max-w-[160px] items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs text-primary hover:bg-primary/10" data-testid={`macro-open-att-${m.id}`}>
                    <Paperclip size={12} className="shrink-0"/><span className="truncate">{m.attachments[0].original_filename}</span>
                  </button>
                )}
                {nAtt > 1 && (
                  <button type="button" onClick={() => setExpanded(isOpen ? null : m.id)} title="Voir les pièces jointes"
                    className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs text-primary hover:bg-primary/10" data-testid={`macro-open-att-${m.id}`}>
                    <Paperclip size={12}/>{nAtt} fichiers
                  </button>
                )}
                <span className="hidden lg:inline text-xs text-muted-foreground w-28 truncate text-right" title={m.author_name}>{m.author_name}</span>
                <span className="hidden sm:inline mono text-xs text-muted-foreground w-14 text-right">{m.usage_count} util.</span>
                {user?.role === "admin" && status !== "approved" && (
                  <Button size="sm" variant="ghost" onClick={() => approve(m)} data-testid={`macro-approve-${m.id}`} className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30" title="Approuver">
                    <CheckCircle size={14}/><span className="hidden xl:inline ml-1">Approuver</span>
                  </Button>
                )}
                {user?.role === "admin" && status === "pending" && (
                  <Button size="sm" variant="ghost" onClick={() => reject(m)} data-testid={`macro-reject-${m.id}`} className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30" title="Rejeter">
                    <XCircle size={14}/><span className="hidden xl:inline ml-1">Rejeter</span>
                  </Button>
                )}
                {canEdit && <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openEdit(m)} data-testid={`edit-macro-${m.id}`} title="Modifier"><PencilSimple size={14}/></Button>}
                {canDelete && <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => remove(m)} data-testid={`del-macro-${m.id}`} title="Supprimer"><Trash size={14}/></Button>}
              </div>
            </div>
            {status === "rejected" && m.rejection_reason && (
              <div className="mt-1 ml-7 text-xs text-red-700 dark:text-red-300 flex items-start gap-1" data-testid={`macro-reject-reason-${m.id}`}>
                <Warning size={12} weight="fill" className="mt-0.5 shrink-0"/><span><b>Motif de rejet :</b> {m.rejection_reason}</span>
              </div>
            )}
            {status === "pending" && isAuthor && user?.role === "technicien" && (
              <div className="mt-1 ml-7 text-xs text-amber-700 dark:text-amber-300">En attente de validation par un administrateur avant d&apos;être utilisable dans les tickets.</div>
            )}
            {isOpen && (
              <p className="mt-2 ml-7 rounded-md bg-muted/50 p-3 text-sm whitespace-pre-wrap" data-testid={`macro-body-${m.id}`}>{m.body}</p>
            )}
            {isOpen && nAtt > 0 && (
              <div className="mt-2 ml-7 flex flex-wrap gap-2" data-testid={`macro-att-list-${m.id}`}>
                {m.attachments.map((a) => (
                  <button key={a.id} type="button" onClick={() => openAttachment(a)}
                    className="inline-flex max-w-[260px] items-center gap-1 rounded-md border bg-card px-2 py-1 text-xs text-primary hover:bg-primary/10" data-testid={`macro-open-att-file-${a.id}`}>
                    <Paperclip size={12} className="shrink-0"/><span className="truncate">{a.original_filename}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          );
        })}
        {macros.length === 0 && <p className="text-muted-foreground text-center py-10">Aucune macro pour le moment.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Modifier la macro" : "Nouvelle macro"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nom</Label>
                <Input required data-testid="macro-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ex. Guide RàZ mot de passe"/>
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="macro-cat"><SelectValue/></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Contenu (peut inclure des variables comme &#123;client_name&#125;)</Label>
              <Textarea required data-testid="macro-body" className="min-h-[220px]"
                value={form.body} onChange={(e) => setForm({...form, body: e.target.value})}
                placeholder="Bonjour {client_name},&#10;&#10;Merci de suivre ces étapes…"/>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Fermer</Button>
              <Button type="submit" data-testid="macro-save">{editing ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </form>

          {/* Attachments section (only when editing an existing macro) */}
          <div className="mt-2 pt-4 border-t space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-caps flex items-center gap-2">
                <Paperclip size={12}/> Pièces jointes {editing ? `(${(editing.attachments || []).length})` : ""}
              </div>
              {editing ? (
                <>
                  <Button size="sm" variant="secondary" type="button"
                    data-testid="macro-attach-btn"
                    onClick={() => fileInputRef.current?.click()} disabled={attBusy}>
                    <Paperclip size={14} className="mr-1.5"/>{attBusy ? "Envoi…" : "Ajouter"}
                  </Button>
                  <input ref={fileInputRef} type="file" multiple className="hidden"
                    data-testid="macro-attach-input"
                    onChange={(e) => uploadFiles(e.target.files)}
                    accept="image/*,.pdf,.txt,.log,.csv,.zip,.doc,.docx,.xls,.xlsx"/>
                </>
              ) : (
                <span className="text-xs text-muted-foreground">Enregistrez d&apos;abord la macro pour ajouter des fichiers.</span>
              )}
            </div>
            {editing && (editing.attachments || []).length === 0 && (
              <p className="text-xs text-muted-foreground">Aucune pièce jointe. Ajoutez des guides PDF, captures d&apos;écran ou modèles à joindre automatiquement (max 15 Mo par fichier).</p>
            )}
            {editing && (editing.attachments || []).length > 0 && (
              <ul className="space-y-2">
                {editing.attachments.map((a) => {
                  const Icon = FILE_ICON(a.content_type);
                  return (
                    <li key={a.id} data-testid={`macro-att-${a.id}`}
                      className="flex items-center gap-3 p-2 rounded-md border bg-muted/30">
                      <Icon size={18} className="text-muted-foreground"/>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{a.original_filename}</div>
                        <div className="text-[0.68rem] text-muted-foreground mono">{humanSize(a.size)}</div>
                      </div>
                      <button type="button" onClick={() => downloadAttachment(a)}
                        data-testid={`macro-att-dl-${a.id}`}
                        className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-foreground">
                        <DownloadSimple size={16}/>
                      </button>
                      <button type="button" onClick={() => removeAttachment(a)}
                        data-testid={`macro-att-del-${a.id}`}
                        className="p-1.5 rounded hover:bg-background text-muted-foreground hover:text-destructive">
                        <X size={16}/>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
